import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { DataGrid, Selection, Column, FilterRow, Scrolling, DataGridRef, DataGridTypes, LoadPanel, Editing } from 'devextreme-react/data-grid';
import { DataChange } from 'devextreme/common/grids';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';

import { formatMessage } from 'devextreme/localization';

import FieldTypeCell from '../field-type-tag/FieldTypeCell';
import DataTypeCell from '../field-type-cell/DataTypeCell';
import useTransformationColumn from '../transformation-popup/useTransformationColumn';
import useFilterColumn from '../filter-popup/useFilterColumn';
import StringHelper from '../../tools/StringHelper';
import FieldNameRules from '../field-name-column/FieldNameRules';

import { TransformationLogic } from '../../logics/transformationmodule/TransformationLogic';


import TransformationTypeDto from '../../classes/dtos/pathcommonmodule/TransformationTypeDto';
import DataTypeDto from '../../classes/dtos/datastructuremodule/DataTypeDto';

import TransformationCapabilitiesDto from '../../classes/dtos/transformationmodule/TransformationCapabilitiesDto';
import PathReportingMappingCapabilitiesDto from '../../classes/dtos/pathreportingmodule/PathReportingMappingCapabilitiesDto';

import ReportingFieldMappingDto from '../../classes/dtos/pathreportingmodule/ReportingFieldMappingDto';


interface FieldGridProps {
    MappingChanges: DataChange[] | undefined;
    SetMappingChanges(changes: DataChange[], origin: ReportingFieldMappingDto[]): void;

    Capabilities: PathReportingMappingCapabilitiesDto[];
    TransformationsCapabilities: TransformationCapabilitiesDto[];
    Mapping: ReportingFieldMappingDto[];
    IsSimpleFilterMode: boolean;
    // Colonne FILTER affichée seulement quand true : feature Feature.FilterVisibility active pour le client
    // ET table source qui gère le filtrage (même calcul `withFilter` que FiltersSelection). Calculé côté page.
    FiltersEnabled: boolean;
    TableReferenceId: number;

    AdvancedFilterMode: boolean;
    SetAdvancedFilterMode(value: boolean): void;
    AdvancedFilterModeLocked: boolean;

    SelectionIds: number[];
    SetSelectionIds(ids: number[]): void;
}

export interface FieldGridHandle {
    Save: () => void;
}

interface GridBodyProps {
    dataGridRef: React.RefObject<DataGridRef>;
    dataSource: DataSource;
    onSelectionChanged: (e: DataGridTypes.SelectionChangedEvent) => void;
    onRowUpdating: (e: DataGridTypes.RowUpdatingEvent) => void;
    onEditingStart: (e: DataGridTypes.EditingStartEvent) => void;
    onRowPrepared: (e: DataGridTypes.RowPreparedEvent) => void;
    onEditingChangesChange: (e: DataChange[]) => void;
    sourceFieldTypeCalculateValue: (rowData: ReportingFieldMappingDto) => string;
    transformationButtonCell: (cellData: DataGridTypes.ColumnCellTemplateData) => JSX.Element;
    transformationCalculateCellValue: (rowData: ReportingFieldMappingDto) => boolean;
    transformationCalculateSortValue: (rowData: ReportingFieldMappingDto) => string;
    targetFieldTypeCellRender: (cellData: DataGridTypes.ColumnCellTemplateData) => JSX.Element;
    targetFieldTypeCalculateCellValue: (rowData: ReportingFieldMappingDto) => DataTypeDto;
    targetFieldTypeCalculateDisplayValue: (rowData: ReportingFieldMappingDto) => string;
    targetFieldTypeCalculateSortValue: (rowData: ReportingFieldMappingDto) => DataTypeDto;
    filterColumnLeaf: JSX.Element;
    FiltersEnabled: boolean;
    IsSimpleFilterMode: boolean;
    SelectionIds: number[];
}

// Isolated from FieldGrid and memoized so the DataGrid tree is untouched by a popup-only re-render:
// useTransformationColumn()/useFilterColumn() own their `popup` visibility state, but since those hooks
// are called directly in FieldGrid's body, that state lives on FieldGrid's own fiber — any setPopup(...)
// re-renders FieldGrid as a whole. Without this memo boundary, React would still re-run every Column's
// render (and DevExtreme would re-diff every row's cell templates) purely because the parent re-rendered,
// even though every prop below is already reference-stable. All the button-cell/calculate* callbacks read
// their live data through refs (see transformationColumnRef / filterColumnRef above), so they stay correct
// even though this component intentionally skips re-rendering when only the popup toggles.
const GridBody = React.memo(({ dataGridRef, dataSource, onSelectionChanged, onRowUpdating, onEditingStart, onRowPrepared, onEditingChangesChange, sourceFieldTypeCalculateValue, transformationButtonCell, transformationCalculateCellValue, transformationCalculateSortValue, targetFieldTypeCellRender, targetFieldTypeCalculateCellValue, targetFieldTypeCalculateDisplayValue, targetFieldTypeCalculateSortValue, filterColumnLeaf, FiltersEnabled, IsSimpleFilterMode, SelectionIds }: GridBodyProps) => (
    <DataGrid
        id="dataGrid"
        ref={dataGridRef}
        dataSource={dataSource}
        columnAutoWidth={false}
        paging={{ enabled: false }}
        onSelectionChanged={onSelectionChanged}
        onRowUpdating={onRowUpdating}
        onEditingStart={onEditingStart}
        onRowPrepared={onRowPrepared}
        height='calc(100vh - 340px)'>
        <Scrolling mode="standard" />
        <Selection mode="multiple" selectAllMode="allPages" showCheckBoxesMode="always" />
        <FilterRow visible={true} />
        <LoadPanel enabled={false} />

        <Editing
            mode="batch"
            allowUpdating={true}
            allowAdding={false}
            allowDeleting={false}
            refreshMode="repaint"
            onChangesChange={onEditingChangesChange}
        />

        <Column caption="SOURCE" cssClass="GridGroupHeader">
            <Column caption="NAME" dataField="Name" dataType="string" allowEditing={false} />
            <Column caption="FIELD TYPE" dataField="Type" dataType="string" allowEditing={false}
                cellRender={FieldTypeCell}
                calculateDisplayValue={sourceFieldTypeCalculateValue}
                calculateSortValue={sourceFieldTypeCalculateValue}
                filterOperations={["contains"]}
            />
        </Column>

        <Column caption="TRANSFORMATION" dataType="boolean" allowEditing={false} allowFiltering={true} width={200} cssClass="GridGroupHeader transformation-cell-td hide-btn-when-unselected"
            cellRender={transformationButtonCell}
            calculateCellValue={transformationCalculateCellValue}
            calculateSortValue={transformationCalculateSortValue}
            trueText={formatMessage("Grid_ColumnFilterWith")}
            falseText={formatMessage("Grid_ColumnFilterWithout")}
        />
        <Column dataField="TransformationType" visible={false} allowEditing={true} />
        <Column dataField="TransformationParameters" visible={false} allowEditing={true} />

        <Column caption="TARGET" cssClass="GridGroupHeader">
            <Column caption="FIELD TYPE" dataType="string" allowEditing={false}
                cellRender={targetFieldTypeCellRender}
                calculateCellValue={targetFieldTypeCalculateCellValue}
                calculateDisplayValue={targetFieldTypeCalculateDisplayValue}
                calculateSortValue={targetFieldTypeCalculateSortValue}
                filterOperations={["contains"]}
            />
            <Column caption="NAME" dataField="DisplayName" dataType="string" allowEditing={true}>
                <FieldNameRules SelectionIds={SelectionIds} Mode="free" />
            </Column>
        </Column>

        {FiltersEnabled && (IsSimpleFilterMode ? (
            <Column caption="FILTER" cssClass="GridGroupHeader">
                {filterColumnLeaf}
            </Column>
        ) : filterColumnLeaf)}

        <Column dataField="Filters" visible={false} allowEditing={true} />
        <Column dataField="DeclaredVariables" visible={false} allowEditing={true} />
    </DataGrid>
));

const FieldGrid = forwardRef<FieldGridHandle, FieldGridProps>(({ MappingChanges, SetMappingChanges, Capabilities, TransformationsCapabilities, Mapping, IsSimpleFilterMode, FiltersEnabled, TableReferenceId, AdvancedFilterMode, SetAdvancedFilterMode, AdvancedFilterModeLocked, SelectionIds, SetSelectionIds }, ref) => {

    const mappingRef = useRef<ReportingFieldMappingDto[]>([]);
    const dataGridRef = useRef<DataGridRef>(null);
    const storeRef = useRef<CustomStore<any>>();
    const dataSourceRef = useRef<DataSource>();

    const silentSelectRef = useRef(false);

    const selectionIdsRef = useRef<number[]>(SelectionIds);

    const [mapping, setMapping] = useState<ReportingFieldMappingDto[]>(Mapping);

    useEffect(() => {
        setMapping(Mapping);
    }, [Mapping]);

    useEffect(() => {
        selectionIdsRef.current = SelectionIds;
    }, [SelectionIds]);

    type UpdateObject = { [key: string]: any };

    function updateObjectInPlace(target: UpdateObject, updates: UpdateObject): void {
        Object.assign(target, updates);
    }

    useImperativeHandle(ref, () => ({
        Save() {
            if (dataGridRef.current) {
                dataGridRef.current.instance().saveEditData();
            }
        }
    }));

    const isRowLocked = useCallback((rowData: ReportingFieldMappingDto): boolean => {
        if (rowData.Type?.Type === 0) { return true; }
        const source = Capabilities.find((e) => e.SourceFieldId === rowData.SourceFieldReferenceId) ?? null;
        return source === null || source.TransformationLogicIds.length === 0;
    }, [Capabilities]);

    // Le CustomStore n'est instancié qu'une fois : son onLoaded doit lire la version courante
    // de isRowLocked (qui dépend de Capabilities, chargées en async), sinon un refresh direct
    // sur l'onglet fige un isRowLocked avec Capabilities=[] -> toutes les lignes "locked" -> tout se sélectionne.
    const isRowLockedRef = useRef(isRowLocked);
    useEffect(() => {
        isRowLockedRef.current = isRowLocked;
    }, [isRowLocked]);

    const onSelectionChanged = useCallback(
        (e: DataGridTypes.SelectionChangedEvent) => {
            if (!silentSelectRef.current) {
                SetSelectionIds(e.selectedRowsData.filter((x) => !isRowLocked(x)).map((x) => x.SourceFieldReferenceId));
            }
        }, [SetSelectionIds, isRowLocked]);

    // garder mapping à jour dans le ref puis recharger la DS
    useEffect(() => {
        mappingRef.current = mapping ?? [];
        dataSourceRef.current?.reload();
    }, [mapping]);

    storeRef.current ??= new CustomStore({
            key: "SourceFieldReferenceId",
            loadMode: "raw",
            load: async () => mappingRef.current, // toujours la version à jour
            update: async (key: any, values: ReportingFieldMappingDto) => {
                const row = mappingRef.current.find(e => e.SourceFieldReferenceId === key);
                if (row) updateObjectInPlace(row, values);
            },
            onLoaded: () => {
                const grid = dataGridRef.current?.instance();
                if (grid) {
                    grid.option("editing.changes", MappingChanges);

                    const mappingNow = mappingRef.current;
                    if (mappingNow?.length) {
                        const locked = mappingNow.filter((e) => isRowLockedRef.current(e)).map(e => e.SourceFieldReferenceId);
                        let selected = Array.from(new Set([...selectionIdsRef.current, ...locked]));
                        if (selectionIdsRef.current.length === 0) { selected = []; }
                        silentSelectRef.current = true;
                        grid.selectRows(selected, false);
                        silentSelectRef.current = false;
                    }
                }
            }
        });

    dataSourceRef.current ??= new DataSource({
            store: storeRef.current!,
            paginate: false,
            reshapeOnPush: true
        });

    const calculateLookupTransformationTypes = useCallback((rowData: ReportingFieldMappingDto) => {

        let transformations = new Array<TransformationTypeDto>();
        if (rowData.SourceFieldReferenceId !== null) {
            let source = Capabilities.find((e) => e.SourceFieldId === rowData.SourceFieldReferenceId) ?? null;
            if (source !== null && source.TransformationLogicIds.length > 0) {
                TransformationsCapabilities.forEach((transformationCapabilities) => {
                    let found = source?.TransformationLogicIds.find((x) => x === transformationCapabilities.LogicId) ?? null;
                    if (found !== null && transformationCapabilities.LogicId !== 0) {
                        transformations.push(new TransformationTypeDto(transformationCapabilities.LogicId, transformationCapabilities.GetDescription()));
                    }
                });
            }
        }

        return transformations;
    }, [Capabilities, TransformationsCapabilities]);

    const calculateTransformationsAvailable = useCallback((rowData: ReportingFieldMappingDto): boolean => {
        let source = Capabilities.find((e) => e.SourceFieldId === rowData.SourceFieldReferenceId) ?? null;
        return (source !== null && source.TransformationLogicIds.length > 0);
    }, [Capabilities]);

    // A transformation is mandatory when the source offers real transformations but not "Direct" (LogicId 0).
    const isTransformationRequired = useCallback((rowData: ReportingFieldMappingDto): boolean => {
        let source = Capabilities.find((e) => e.SourceFieldId === rowData.SourceFieldReferenceId) ?? null;
        return source !== null
            && source.TransformationLogicIds.length > 0
            && source.TransformationLogicIds.indexOf(0) === -1;
    }, [Capabilities]);

    const getOptionsDetailsForPopup = useCallback((type: number | null) =>
        TransformationLogic.GetOptionsDetailsForReporting({ TransformationType: type } as ReportingFieldMappingDto, TransformationsCapabilities),
        [TransformationsCapabilities]);

    const transformationColumn = useTransformationColumn({
        rowKeyField: 'SourceFieldReferenceId',
        typeField: 'TransformationType',
        parametersField: 'TransformationParameters',
        title: formatMessage("TransformationPopup_TitleTransformation"),
        transformationsCapabilities: TransformationsCapabilities,
        calculateAvailableTypes: calculateLookupTransformationTypes,
        getOptionsDetails: getOptionsDetailsForPopup,
        isTransformationRequired
        // The button is hidden on unselected rows purely in CSS (see TransformationPopup.css,
        // `.hide-btn-when-unselected`), so no isRowActive / selection-driven repaint here.
    });

    // useTransformationColumn() re-runs on every render regardless (its own popup state lives here),
    // so `transformationColumn` is a fresh object every time. Read it through a ref so the DataGrid
    // column props below can stay reference-stable across a popup-open re-render — otherwise every
    // column whose cellRender/calculateCellValue derives from it "changes" on each render and forces
    // DevExtreme to reconcile/repaint the whole (unpaginated, all-rows-rendered) grid before the popup
    // even shows.
    const transformationColumnRef = useRef(transformationColumn);
    transformationColumnRef.current = transformationColumn;

    const transformationButtonCell = useCallback(
        (cellData: DataGridTypes.ColumnCellTemplateData) => transformationColumnRef.current.ButtonCell(cellData), []);
    const transformationCalculateCellValue = useCallback(
        (rowData: ReportingFieldMappingDto) => transformationColumnRef.current.calculateDisplayValue(rowData).trim().length > 0, []);
    const transformationCalculateSortValue = useCallback(
        (rowData: ReportingFieldMappingDto) => transformationColumnRef.current.calculateDisplayValue(rowData), []);
    const transformationGetEffectiveType = useCallback(
        (cellDataOrRow: any) => transformationColumnRef.current.getEffectiveType(cellDataOrRow), []);

    // Derive the TARGET field type from the given transformation type. The cellRender path passes the
    // effective type (pending batch edits included, read live from the grid); the sort / filter paths
    // pass the persisted rowData.TransformationType (they have no grid handle), so they stay on the
    // saved value until the next reload.
    const getOutputDataType = useCallback((rowData: ReportingFieldMappingDto, transformationType: number | null): DataTypeDto => {

        if (rowData.Type === undefined || rowData.Type === null) {
            return new DataTypeDto(0, false, "NO TYPE");
        }

        if (transformationType === null || transformationType === 0) {
            return new DataTypeDto(rowData.Type.Type, rowData.Type.IsMultiple, rowData.Type.TypeLabel);
        }

        let transformationCapabilities = TransformationsCapabilities.find((x) => x.LogicId === transformationType);

        return transformationCapabilities?.TargetDataType ?? new DataTypeDto(0, false, "Unmanaged");
    }, [TransformationsCapabilities]);

    const targetFieldTypeCellRender = useCallback((cellData: DataGridTypes.ColumnCellTemplateData) =>
        DataTypeCell({ ...cellData, value: getOutputDataType(cellData.data, transformationGetEffectiveType(cellData)) }),
        [getOutputDataType, transformationGetEffectiveType]);
    const targetFieldTypeCalculateCellValue = useCallback(
        (rowData: ReportingFieldMappingDto) => getOutputDataType(rowData, rowData.TransformationType), [getOutputDataType]);
    const targetFieldTypeCalculateDisplayValue = useCallback(
        (rowData: ReportingFieldMappingDto) => getOutputDataType(rowData, rowData.TransformationType).Label, [getOutputDataType]);
    const targetFieldTypeCalculateSortValue = useCallback(
        (rowData: ReportingFieldMappingDto) => getOutputDataType(rowData, rowData.TransformationType), [getOutputDataType]);

    const handleOnChangesChange = useCallback((e: DataChange[]): void => {
        SetMappingChanges(e, mappingRef.current);
    }, [SetMappingChanges]);

    const filterColumn = useFilterColumn({
        title: formatMessage("FilterColumn_Title"),
        rowKeyField: 'SourceFieldReferenceId',
        tableReferenceId: TableReferenceId,
        getRowFieldReferenceId: (rowData: ReportingFieldMappingDto) => rowData?.SourceFieldReferenceId ?? 0,
        getRowOriginFieldType: (rowData: ReportingFieldMappingDto) => rowData?.Type?.Type ?? 0,
        getPrefillTechnicalName: (rowData: ReportingFieldMappingDto) => StringHelper.ToLabelForApi(rowData?.Name ?? ""),
        advancedMode: AdvancedFilterMode,
        onAdvancedModeChange: SetAdvancedFilterMode,
        advancedModeLocked: AdvancedFilterModeLocked
    });

    // Same reasoning as transformationColumnRef above: useFilterColumn()'s own popup state forces a
    // fresh `filterColumn` object on every render, even though its ButtonCell/HeaderCell/
    // calculateDisplayValue are internally memoized. Read it through a ref so this grid's own wrapper
    // functions keep stable identities too.
    const filterColumnRef = useRef(filterColumn);
    filterColumnRef.current = filterColumn;

    const filterButtonCell = useCallback(
        (cellData: DataGridTypes.ColumnCellTemplateData) => filterColumnRef.current.ButtonCell(cellData), []);
    // HeaderCell is memoised inside useFilterColumn and only changes identity when the checkbox state changes, so
    // passing it directly makes DevExtreme repaint the header with the current checked value.
    const filterHeaderCell = filterColumn.HeaderCell;
    const filterDisplayValue = useCallback(
        (rowData: ReportingFieldMappingDto) => filterColumnRef.current.calculateDisplayValue(rowData), []);

    // Same column either way; in "simple" mode it lives inside a FILTER band whose sub-header carries the
    // "Advanced mode" checkbox, otherwise it is a standalone group-header column.
    // The FILTER cell shows a button, not text: for the filter row we expose a boolean "configured or
    // not" so DevExtreme renders an "Avec / Sans" select instead of a free-text "contains" box.
    const filterIsConfigured = useCallback(
        (rowData: ReportingFieldMappingDto) => filterDisplayValue(rowData).trim().length > 0, [filterDisplayValue]);

    const filterColumnLeaf = React.useMemo(() => (
        <Column caption="FILTER" dataType="boolean" allowEditing={false} allowFiltering={true} allowSorting={false} width={210}
            cssClass={IsSimpleFilterMode ? "filter-cell-td hide-btn-when-unselected" : "GridGroupHeader filter-cell-td hide-btn-when-unselected"}
            headerCellRender={IsSimpleFilterMode ? filterHeaderCell : undefined}
            cellRender={filterButtonCell}
            calculateCellValue={filterIsConfigured}
            calculateSortValue={filterDisplayValue}
            trueText={formatMessage("Grid_ColumnFilterWith")}
            falseText={formatMessage("Grid_ColumnFilterWithout")}
        />
    ), [IsSimpleFilterMode, filterHeaderCell, filterButtonCell, filterIsConfigured, filterDisplayValue]);

    const handleOnRowUpdating = useCallback((e: DataGridTypes.RowUpdatingEvent) => {
        e.newData = { ...e.oldData, ...e.newData };
    }, []);

    const handleOnEditingStart = useCallback((e: DataGridTypes.EditingStartEvent) => {
        e.cancel = e.data.Type.Type === 0;
    }, []);

    const handleOnRowPrepared = useCallback((e: DataGridTypes.RowPreparedEvent) => {
        if (e.rowType === "data" && (e.data?.Type?.Type === 0 || !calculateTransformationsAvailable(e.data))) {
            e.rowElement.classList.add("unmanaged");
            e.rowElement.style.opacity = "0.5";
            e.rowElement.style.cursor = "default";
            e.rowElement.style.pointerEvents = "none";
            e.rowElement.setAttribute("aria-hidden", "true");
        }
    }, [calculateTransformationsAvailable]);

    const sourceFieldTypeCalculateValue = useCallback(
        (rowData: ReportingFieldMappingDto) => rowData.Type?.TypeLabel ?? "???", []);

    return (
        <div className={"block-card datagrid-without-header"}>
            <GridBody
                dataGridRef={dataGridRef}
                dataSource={dataSourceRef.current!}
                onSelectionChanged={onSelectionChanged}
                onRowUpdating={handleOnRowUpdating}
                onEditingStart={handleOnEditingStart}
                onRowPrepared={handleOnRowPrepared}
                onEditingChangesChange={handleOnChangesChange}
                sourceFieldTypeCalculateValue={sourceFieldTypeCalculateValue}
                transformationButtonCell={transformationButtonCell}
                transformationCalculateCellValue={transformationCalculateCellValue}
                transformationCalculateSortValue={transformationCalculateSortValue}
                targetFieldTypeCellRender={targetFieldTypeCellRender}
                targetFieldTypeCalculateCellValue={targetFieldTypeCalculateCellValue}
                targetFieldTypeCalculateDisplayValue={targetFieldTypeCalculateDisplayValue}
                targetFieldTypeCalculateSortValue={targetFieldTypeCalculateSortValue}
                filterColumnLeaf={filterColumnLeaf}
                FiltersEnabled={FiltersEnabled}
                IsSimpleFilterMode={IsSimpleFilterMode}
                SelectionIds={SelectionIds}
            />

            {transformationColumn.PopupElement}
            {FiltersEnabled && filterColumn.PopupElement}
        </div>
    );
});

export default FieldGrid;